/**
 * School Year Modal Component
 * Coláiste Lurgan Management System - School Year creation and editing
 */

import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useToast } from '../../hooks/use-toast';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../ui/form';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { useCreateSchoolYear, useUpdateSchoolYear } from '../../hooks/useSchoolYears';
import { GraduationCap } from 'lucide-react';

// Form validation schema
const schoolYearSchema = z.object({
  year: z.string()
    .min(1, 'School year name is required')
    .max(20, 'School year name must be 20 characters or less'),
  display_order: z.number()
    .int('Display order must be a whole number')
    .min(0, 'Display order cannot be negative')
    .default(0),
  is_active: z.boolean().default(true),
});

export function SchoolYearModal({
  isOpen,
  onClose,
  schoolYear = null,
  onSuccess
}) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const isEditing = !!schoolYear;

  // Mutations
  const createSchoolYearMutation = useCreateSchoolYear();
  const updateSchoolYearMutation = useUpdateSchoolYear();

  // Form setup
  const form = useForm({
    resolver: zodResolver(schoolYearSchema),
    defaultValues: {
      year: '',
      display_order: 0,
      is_active: true,
    },
  });

  // Pre-populate form when editing
  useEffect(() => {
    if (isEditing && schoolYear) {
      form.reset({
        year: schoolYear.year || '',
        display_order: schoolYear.display_order || 0,
        is_active: schoolYear.is_active ?? true,
      });
    } else if (!isEditing) {
      form.reset({
        year: '',
        display_order: 0,
        is_active: true,
      });
    }
  }, [schoolYear, isEditing, form]);

  // Handle form submission
  const onSubmit = async (data) => {
    try {
      const schoolYearData = {
        year: data.year,
        display_order: Number(data.display_order) || 0,
        is_active: Boolean(data.is_active),
      };

      if (isEditing) {
        await updateSchoolYearMutation.mutateAsync({
          id: schoolYear.uuid,
          ...schoolYearData,
        });
      } else {
        await createSchoolYearMutation.mutateAsync(schoolYearData);
      }

      // Close modal and trigger success callback
      onClose();
      form.reset();
      if (onSuccess) {
        onSuccess(data, isEditing);
      }
    } catch (error) {
      console.error('School year submission error:', error);

      // Extract error message from API response
      let errorMessage = t('schoolYears.error.saveFailed', 'Failed to save school year.');

      if (error.response?.data) {
        const data = error.response.data;
        if (data.year) {
          errorMessage = Array.isArray(data.year) ? data.year[0] : data.year;
        } else if (data.display_order) {
          errorMessage = Array.isArray(data.display_order) ? data.display_order[0] : data.display_order;
        } else if (data.detail) {
          errorMessage = data.detail;
        } else if (data.non_field_errors) {
          errorMessage = Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors;
        }
      }

      toast({
        title: t('common.error', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  // Handle modal close
  const handleClose = () => {
    form.reset();
    onClose();
  };

  const isPending = createSchoolYearMutation.isPending || updateSchoolYearMutation.isPending;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GraduationCap className="h-5 w-5" />
            {isEditing
              ? t('schoolYears.modal.editTitle', 'Edit School Year')
              : t('schoolYears.modal.createTitle', 'Create School Year')
            }
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? t('schoolYears.modal.editDescription', 'Update the school year information below.')
              : t('schoolYears.modal.createDescription', 'Create a new school year for student registration.')
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="year"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('schoolYears.fields.year', 'School Year')} *
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t('schoolYears.placeholders.year', 'e.g., 1st Year, 2nd Year, Leaving Cert')}
                      {...field}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="display_order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('schoolYears.fields.displayOrder', 'Display Order')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      placeholder="0"
                      onFocus={(e) => e.target.value && e.target.select()}
                      {...field}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || value === null) {
                          field.onChange(0);
                        } else {
                          const numValue = parseInt(value, 10);
                          field.onChange(isNaN(numValue) ? 0 : numValue);
                        }
                      }}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isPending}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-normal">
                      {t('schoolYears.fields.isActive', 'Active')}
                    </FormLabel>
                    <p className="text-xs text-muted-foreground">
                      {t('schoolYears.fields.isActiveDescription', 'Whether this school year is currently selectable for new registrations')}
                    </p>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isPending}
              >
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isPending}
                className="gap-2"
              >
                <GraduationCap className="h-4 w-4" />
                {isPending
                  ? (isEditing ? t('common.updating', 'Updating...') : t('common.creating', 'Creating...'))
                  : (isEditing ? t('common.update', 'Update') : t('common.create', 'Create'))
                }
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default SchoolYearModal;